// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // Ensure framer-motion is installed: npm install framer-motion
import Header from '../components/Header';
import HowToUse from '../components/HowToUse';
import LoadingScreen from '../components/LoadingScreen';

// Mock data for occasions with icons
const occasions = [
  { title: 'Рожден ден', description: 'Персонализирани пожелания за специалния ден.', icon: '🎂' },
  { title: 'Сватба', description: 'Романтични видеа за незабравими спомени.', icon: '💒' },
  { title: 'Коледа', description: 'Празнични поздрави с топлина и магия.', icon: '🎄' },
  { title: 'Св. Валентин', description: 'Любовни послания, които докосват сърцето.', icon: '❤️' },
  { title: 'Годишнина', description: 'Спомени и обещания за бъдещето.', icon: '💑' },
  { title: 'Дипломиране', description: 'Поздравления за постигнатите успехи.', icon: '🎓' },
];

// Mock data for who we are (updated with inspiring story)
const aboutUs = {
  title: 'Как започна всичко?',
  description: 'Аз съм Димитър Джукелов, създателят на EvoWishes. Идеята за тази платформа се роди от личен момент – исках да подаря на близък човек нещо повече от обикновена картичка. Вдъхновен от емоциите, които видеата могат да предизвикат, реших да съчетая традиционните поздрави с модерна технология. EvoWishes е плод на страстта ми към иновациите и желанието да правя света по-топъл – всяка картичка е възможност за незабравим спомен.',
  creatorImage: '../assets/creator-photo.jpg', // Replace with your actual photo URL
};

// Mock data for testimonials
const testimonials = [
  {
    quote: "EvoWishes направи поздравите ми незабравими!",
    author: "Ана Иванова",
  },
  {
    quote: "Иновативно и лесно за използване – препоръчвам!",
    author: "Петър Георгиев",
  },
  {
    quote: "Перфектно за всеки повод – емоционално и уникално.",
    author: "Мария Стоянова",
  },
  {
    quote: "Най-доброто решение за персонализирани картички!",
    author: "Иван Петров",
  },
  {
    quote: "Видеото добавя магия към всяко пожелание.",
    author: "Светлана Димитрова",
  },
  {
    quote: "Лесно и забавно – идеално за празници!",
    author: "Николай Иванов",
  },
];

// Steps data for "Как да използвате картичките"
const steps = [
  { number: 1, title: 'Закупете картичката', description: 'Нашите иновативни картички може да намерите при нашите партньори в търговската мрежа.' },
  { number: 2, title: 'Сканирайте баркода', description: 'С вашият мобилен телефон сканирайте баркода, който се намира от вътрешната страна на картичката.' },
  { number: 3, title: 'Въведете кода за защита', description: 'След като сте сканирали баркода от вашата картичка, въвдете паролата която е позиционирана под него.' },
  { number: 4, title: 'Качете вашият персонализиран поздрав', description: 'Нашата иновативна картичка ще ви даде възможност за заснемане или качване на клип.' },
  { number: 5, title: 'Готово!', description: 'Магията се случи. При следващо сканиране ще се визуализира вашето съдържание, като има възможност за изтриване и сваляне.' },
];

export default function Home() {
  const [videoError, setVideoError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Video source (replace with your uploaded video URL from Django media)
  const videoSource = '/media/videos/promo-video.mp4'; // Update with your actual file path

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, staggerChildren: 0.2 } },
  };

  const slideLeft = {
    hidden: { x: -100, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.8 } },
  };

  const slideRight = {
    hidden: { x: 100, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.8 } },
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 5000); // 5-second loading screen
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = document.querySelector('video');
    if (video) {
      video.addEventListener('error', () => setVideoError('Грешка при зареждане на видеото. Моля, проверете връзката си или контактуйте поддръжка.'));
    }
    return () => {
      if (video) video.removeEventListener('error', () => {});
    };
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col font-sans">
      <Header />

      {/* 2. Video Section - Full-width embedded video */}
      <motion.section
        className="relative w-full"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.video
          className="w-full h-auto object-cover max-h-[70vh]"
          autoPlay
          muted
          loop
          playsInline
          variants={fadeIn}
          onError={() => setVideoError('Грешка при зареждане на видеото.')}
        >
          <source src={videoSource} type="video/mp4" />
          {videoError && <p className="text-white text-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">{videoError}</p>}
          Вашият браузър не поддържа видео тага.
        </motion.video>
        {videoError && !document.querySelector('video')?.error && (
          <div className="text-center text-red-600 py-4">{videoError}</div>
        )}
      </motion.section>

      {/* 3. Designed Text Section - "Не просто пожелание. А преживяване!" */}
      <motion.section
        className="py-16 px-4 bg-gray-50 relative overflow-hidden"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.div
          className="absolute inset-0 bg-red-50/20"
          animate={{ scale: [1, 1.05, 1], opacity: [0.2, 0.3, 0.2] }}
          transition={{ duration: 4, repeat: Infinity, repeatType: "reverse" }}
        />
        <motion.div
          className="relative z-10 text-center max-w-4xl mx-auto"
          variants={fadeIn}
        >
          <motion.h1
            className="text-4xl md:text-6xl font-extrabold text-red-600 mb-4 leading-tight"
            variants={fadeIn}
          >
            Не просто пожелание.<br />А преживяване!
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-gray-800 mb-6"
            variants={fadeIn}
          >
            Преобразете всеки момент с нашите иновативни картички – идеални за изненада, която оставя следа. Направете го лично и запомнящо се!
          </motion.p>
        </motion.div>
      </motion.section>

      {/* 4. Suitable Occasions Section */}
      <motion.section
        className="py-16 px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.h2
          className="text-3xl font-bold text-red-600 text-center mb-12"
          variants={fadeIn}
        >
          Част от Подходящите Поводи
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {occasions.map((occasion, index) => (
            <motion.div
              key={index}
              className="relative w-full h-64 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 flex flex-col items-center justify-center bg-gray-50"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={index % 2 === 0 ? slideLeft : slideRight} // Alternate left/right animation
            >
              <div className="text-6xl mb-4 text-red-600 animate-bounce-slow">{occasion.icon}</div> {/* Large icon with bounce */}
              <h3 className="text-xl font-semibold text-red-600 mb-2">{occasion.title}</h3>
              <p className="text-gray-800 text-sm text-center px-4">{occasion.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 5. How to Use Section - Redesigned as Steps */}
      <motion.section
        className="py-16 px-4 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.h2
          className="text-3xl font-bold text-red-600 text-center mb-12"
          variants={fadeIn}
        >
          Как да Използвате Картичките
        </motion.h2>
        <div className="space-y-8 max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 flex items-start"
              variants={fadeIn}
            >
              <div className="w-12 h-12 flex-shrink-0 bg-red-600 text-white rounded-full flex items-center justify-center mr-4">
                <span className="text-xl font-bold">{step.number}</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-red-600 mb-2">{step.title}</h3>
                <p className="text-gray-800 text-base">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Redesigned Who We Are Section with white background, photo on left, text on right */}
      <motion.section
        className="py-20 px-4 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.div
          className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8"
          variants={fadeIn}
        >
          {/* Creator Photo on Left */}
          <motion.img
            src={aboutUs.creatorImage} // Replace with your actual photo URL
            alt="Създателят на EvoWishes"
            className="w-48 h-48 md:w-64 md:h-64 rounded-full shadow-lg"
            variants={fadeIn}
            whileHover={{ scale: 1.05 }}
          />
          {/* Text on Right */}
          <div className="text-center md:text-left">
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-red-600 mb-6"
              variants={fadeIn}
            >
              {aboutUs.title}
            </motion.h2>
            <motion.p
              className="text-lg md:text-xl text-gray-800 mb-6 leading-relaxed"
              variants={fadeIn}
            >
              {aboutUs.description}
            </motion.p>
          </div>
        </motion.div>
      </motion.section>

      {/* Testimonials Section - Below Who We Are */}
      <motion.section
        className="py-16 px-4 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.h2
          className="text-3xl font-bold text-red-600 text-center mb-12"
          variants={fadeIn}
        >
          Какво Казват Нашите Клиенти
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="p-6 bg-white rounded-lg shadow text-center"
              variants={fadeIn}
            >
              <p className="text-gray-800 mb-4">"{testimonial.quote}"</p>
              <p className="font-semibold text-red-600">{testimonial.author}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Become a Representative Section */}
      <motion.section
        className="py-16 px-4 text-center bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={slideUp}
      >
        <motion.h2
          className="text-3xl font-bold text-red-600 mb-4"
          variants={fadeIn}
        >
          Станете Представител
        </motion.h2>
        <motion.p
          className="text-lg text-gray-800 max-w-2xl mx-auto mb-6"
          variants={fadeIn}
        >
          Ако искате да станете представител и да предлагате EvoWishes в вашия магазин, свържете се с нас!
        </motion.p>
        <motion.div
          className="flex flex-col items-center space-y-2"
          variants={fadeIn}
        >
          <p className="text-red-600 font-semibold">Телефон: +359 888 133 388</p>
          <p className="text-red-600 font-semibold">Имейл: partner@evowishes.com</p>
        </motion.div>
      </motion.section>

      <footer className="py-6 text-center text-white bg-red-600">
        <p>&copy; 2025 EvoWishes. Всички права запазени.</p>
        <div className="mt-4 flex justify-center space-x-4 text-sm">
          <Link to="/terms" className="text-white hover:underline">Условия</Link>
          <Link to="/privacy" className="text-white hover:underline">Поверителност</Link>
          <Link to="/contact" className="text-white hover:underline">Контакт</Link>
        </div>
      </footer>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s infinite ease-in-out;
        }
      `}</style>
    </div>
  );
}